import {
  AdminServiceGetPartnerDeveloperListProps,
  AdminServiceGetPartnerListProps,
  AdminServiceGetPartnerScopeListProps,
  AdminServiceGetRelatedOrganisationsListProps,
  AdminServiceGetSimilarOrganisationsListProps,
  AdminServiceGetValidationSubmissionDocumentsProps,
  AdminServiceRequestMarkDocumentAsFraudProps,
  AutomatedVettingEventResults,
  AvailableLocales,
  Claim,
  CreateOrganizationProps,
  CreateStaticLinkPayload,
  UpdateStaticLinkPayload,
  FetchClaimsParams,
  FetchMonitoringParams,
  FetchOrganisationsParams,
  FetchRegistriesParams,
  FetchVettingEventsParams,
  FraudCheckResultsResponse,
  GetSignedUrlForDocumentBody,
  StaticLink,
  GetSignedUrlForDocumentProps,
  GetValidationsRequestProps,
  MonitoringList,
  Organisation,
  OrganisationDefaultLocale,
  OrganisationFiles,
  OrganisationLogoBody,
  OrganisationsBatchUpdateProps,
  DonationGiftNoteConfigValue,
  DonationMatchingDirectToCampaignConfigValue,
  DonationMatchingDonationDateEligibilityConfigValue,
  DonationMatchingFamilialConfigValue,
  DonationRestrictedCountriesConfigValue,
  DonationTransactionalEmailsConfigValue,
  FundraisingPublicFundraisersConfigValue,
  GivingDatePeriodConfigurationValue,
  GivingFeaturedConfigValue,
  GivingFeaturesConfigValue,
  GivingHelpLinkConfigValue,
  GivingSpotlightedOrgsConfigValue,
  GivingTransactionalEmailsConfigValue,
  GivingThemeConfigValue,
  GivingHomePageBannerContentConfigValue,
  GivingRegionsConfigValue,
  Partner,
  PartnerConfigRecord,
  PartnerScope,
  VolunteeringAllowanceConfigValue,
  PatchOrganisationParams,
  PatchValidationRequestParams,
  ReviewValidationRequestWebsiteParams,
  PostOrganisationLogoProps,
  PostRejectValidationRequest,
  RegistriesType,
  SubmitValidationRequestQcParams,
  SimilarOrganisation,
  SubmitVettingEventsPayload,
  UpdateOrganisationPaymentMethodParams,
  UpdateGivingConfigPayload,
  UpdateDonationConfigPayload,
  UpdateFundraisingConfigPayload,
  UpdateDonationMatchingConfigPayload,
  UploadOrganisationFilesProps,
  Validation,
  ValidationRequestLogsResponse,
  ValidationSubmissionDocument,
  VettingEvent,
  adminServiceCapabilities
} from '@percent/admin-dashboard/api/types'
import { CurrencyCode } from '@percent/admin-dashboard/constants/currencyCode'
import { PartnerScopes } from '@percent/admin-dashboard/constants/scopes'
import { PBPGetResponse, PBPListResponse, getDataFromPBPResponse } from '@percent/admin-dashboard/services/pbpResponses'
import { OrganisationRelationship } from '@percent/domain/verifications'
import { throttleAsync } from '@percent/utility'

export const adminService = ({ percentClient }: adminServiceCapabilities) => ({
  getOrganisation: ({ id }: { id?: string | null }) =>
    percentClient.get<PBPGetResponse<Organisation>>(`/v1/admin/organisations/${id}`),
  editOrganisation: ({ payload, id }: PatchOrganisationParams) =>
    percentClient.patch(`/v1/admin/organisations/${id}`, payload),
  getOrganisations: (params: FetchOrganisationsParams) =>
    percentClient.get<PBPListResponse<Organisation>>('/v1/admin/organisations', {
      params
    }),
  getOrganisationDefaultLocale: ({ id }: { id: string }) =>
    percentClient.get<PBPGetResponse<OrganisationDefaultLocale>>(
      `/v1/admin/experience/organisations/${id}/default-locale`
    ),
  getOrganisationFiles: ({ id }: { id: string }) =>
    percentClient.get<PBPGetResponse<OrganisationFiles[]>>(`/v1/admin/experience/organisations/${id}/images`),
  uploadOrganisationFile: ({ payload, id }: UploadOrganisationFilesProps) =>
    percentClient.post(`/v1/admin/experience/organisations/${id}/images`, payload.formData),
  updateOrganisationDefaultLocale: ({ payload, id }: { payload: object; id: string }) =>
    percentClient.put(`/v1/admin/experience/organisations/${id}/default-locale`, payload),
  getAvailableLocales: () =>
    percentClient.get<PBPGetResponse<AvailableLocales>>(`/v1/admin/experience/available-locales`),
  getClaims: (params: FetchClaimsParams) => percentClient.get<PBPListResponse<Claim>>('/v1/admin/claims', { params }),
  getOrganisationPaymentMethod: ({ id }: { id: string }) =>
    percentClient.get(`/v1/admin/organisations/${id}/payment-method`, {
      params: {
        timestamp: Date.now()
      }
    }),
  updateOrganisationPaymentMethod: ({ payload, id }: UpdateOrganisationPaymentMethodParams) =>
    percentClient.put(`/v1/admin/organisations/${id}/payment-method`, payload),
  getVettingRequest: ({ id }: { id: string }) => percentClient.get(`/v1/admin/vetting-requests/${id}`),
  getRegistries: (params: FetchRegistriesParams) =>
    percentClient.get<PBPListResponse<RegistriesType>>(`v1/admin/registries`, { params }),
  unreject: ({ id }: { id: string }) => percentClient.patch(`/v1/iam/claim/${id}/unreject`),
  getValidations: throttleAsync(
    (params: GetValidationsRequestProps) =>
      percentClient.get<PBPListResponse<Validation>>('v1/admin/validation-requests', { params }),
    1000,
    { trailing: true, leading: true }
  ),
  getValidationRequest: ({ id }: { id?: string | null }) =>
    percentClient.get<PBPGetResponse<Validation>>(`v1/admin/validation-requests/${id}`),
  getValidationRequestLogs: ({ id }: { id: string }) =>
    percentClient.get<PBPGetResponse<ValidationRequestLogsResponse>>(`/v1/admin/validation-requests/${id}/logs`),
  editValidationRequest: ({ payload, id }: PatchValidationRequestParams) =>
    percentClient.patch(`/v1/admin/validation-requests/${id}`, payload),
  reviewValidationRequestWebsite: ({ id, payload }: ReviewValidationRequestWebsiteParams) =>
    percentClient.post(`/v1/admin/validation-requests/${id}/review-website`, payload),
  getVRFraudCheckResults: ({ id }: { id: string }) =>
    percentClient.get<PBPGetResponse<FraudCheckResultsResponse>>(
      `/v1/admin/validation-requests/${id}/fraud-check-results`
    ),
  rejectValidationRequest: ({ payload, id }: { payload: PostRejectValidationRequest; id: string }) =>
    percentClient.post(`v1/admin/validation-requests/${id}/reject`, payload),
  submitValidationRequestQc: ({ id, payload }: SubmitValidationRequestQcParams) =>
    percentClient.post(`v1/admin/validation-requests/${id}/qc`, payload),
  getVettingEvents: (params: FetchVettingEventsParams) =>
    percentClient.get<PBPListResponse<VettingEvent>>('/v1/admin/vetting-events', { params }),
  getVettingEventsResults: ({ organisationId }: { organisationId: string }) =>
    percentClient.get<PBPGetResponse<AutomatedVettingEventResults>>(
      `/v1/admin/vetting-events/results/${organisationId}`
    ),
  submitVettingEvents: ({ organisationId, payload }: { organisationId: string; payload: SubmitVettingEventsPayload }) =>
    percentClient.post(`v1/admin/organisations/${organisationId}/submit-vetting-events`, payload),
  getMonitoringSubscriptions: throttleAsync(
    (params: FetchMonitoringParams) =>
      percentClient.get<PBPListResponse<MonitoringList>>('v1/admin/monitoring', { params }),
    1000,
    { leading: true, trailing: true }
  ),
  getRelatedOrganisations: (params: AdminServiceGetRelatedOrganisationsListProps) =>
    percentClient.get<PBPListResponse<OrganisationRelationship>>(`/v1/admin/validation/related-organisations`, {
      params
    }),
  getSimilarOrganisations: (params: AdminServiceGetSimilarOrganisationsListProps) =>
    percentClient.get<PBPListResponse<SimilarOrganisation>>(`/v1/admin/validation/similar-organisations`, {
      params
    }),
  getValidationSubmissionDocuments: ({
    validationSubmissionId,
    ...params
  }: AdminServiceGetValidationSubmissionDocumentsProps) =>
    percentClient.get<PBPListResponse<ValidationSubmissionDocument>>(
      `/v1/admin/validation-submissions/${validationSubmissionId}/documents`,
      {
        params
      }
    ),
  requestMarkDocumentAsFraud: ({ id, payload }: AdminServiceRequestMarkDocumentAsFraudProps) =>
    percentClient.patch(`v1/admin/validation-submission-documents/${id}/fraud`, payload),
  getSignedUrlForDocument: ({ documentUrl }: GetSignedUrlForDocumentProps) => {
    let url = documentUrl

    /**
     * HACK
     *
     * This is a temporary fix to handle the fact that the backend
     * is returning absolute URLs not matching the current hostname
     * having the effect of breaking the CORS policy.
     */
    if (window.location.hostname.includes('goodstack.io')) {
      url = url.replace('poweredbypercent.com', 'goodstack.io')
    }

    if (window.location.hostname.includes('poweredbypercent.com')) {
      url = url.replace('goodstack.io', 'poweredbypercent.com')
    }

    return percentClient.get<PBPGetResponse<GetSignedUrlForDocumentBody>>(url)
  },
  queries: {
    getPartnerDeveloperList: ({ partnerId, filter, ...params }: AdminServiceGetPartnerDeveloperListProps) =>
      percentClient.get<PBPListResponse<any>>(`/v1/admin/partners/${partnerId}/accounts`, {
        params: {
          ...params,
          ...(filter ? { filter } : {})
        }
      }),
    getPartnerDetail: ({ id }: { id?: string | null }) =>
      percentClient.get<PBPGetResponse<Partner>>(`/v1/admin/partners/${id}`),
    getPartnerList: ({ ...params }: AdminServiceGetPartnerListProps) =>
      percentClient.get<PBPListResponse<any>>('/v1/admin/partners', { params }),
    getPartnerScopeList: ({ partnerId, ...params }: AdminServiceGetPartnerScopeListProps) =>
      percentClient.get<PBPListResponse<PartnerScope>>(`/v1/admin/partners/${partnerId}/scopes`, { params }),
    getStaticLinks: ({ partnerId, ...params }: { partnerId: string; pageSize?: number | null }) =>
      percentClient.get<PBPGetResponse<StaticLink[]>>(`/v1/admin/partners/${partnerId}/static-links`, { params }),
    getPartnerGivingFeaturesConfig: ({ partnerId }: { partnerId: string }) =>
      percentClient.get<PBPGetResponse<PartnerConfigRecord<GivingFeaturesConfigValue>>>(
        `/v1/admin/partners/${partnerId}/giving/config/features`
      ),
    getPartnerGivingEmailsConfig: ({ partnerId }: { partnerId: string }) =>
      percentClient.get<PBPGetResponse<PartnerConfigRecord<GivingTransactionalEmailsConfigValue>>>(
        `/v1/admin/partners/${partnerId}/giving/config/transactional_emails`
      ),
    getPartnerDonationGiftNoteConfig: ({ partnerId }: { partnerId: string }) =>
      percentClient.get<PBPGetResponse<PartnerConfigRecord<DonationGiftNoteConfigValue>>>(
        `/v1/admin/partners/${partnerId}/donation/config/gift_note`
      ),
    getPartnerDonationEmailsConfig: ({ partnerId }: { partnerId: string }) =>
      percentClient.get<PBPGetResponse<PartnerConfigRecord<DonationTransactionalEmailsConfigValue>>>(
        `/v1/admin/partners/${partnerId}/donation/config/transactional_emails`
      ),
    getPartnerFundraisingPublicFundraisersConfig: ({ partnerId }: { partnerId: string }) =>
      percentClient.get<PBPGetResponse<PartnerConfigRecord<FundraisingPublicFundraisersConfigValue>>>(
        `/v1/admin/partners/${partnerId}/fundraising/config/public_fundraisers`
      ),
    getPartnerDonationMatchingFamilialConfig: ({ partnerId }: { partnerId: string }) =>
      percentClient.get<PBPGetResponse<PartnerConfigRecord<DonationMatchingFamilialConfigValue>>>(
        `/v1/admin/partners/${partnerId}/donation-matching/config/familial_donation_matching`
      ),
    getPartnerDonationMatchingDonationDateEligibilityConfig: ({ partnerId }: { partnerId: string }) =>
      percentClient.get<PBPGetResponse<PartnerConfigRecord<DonationMatchingDonationDateEligibilityConfigValue>>>(
        `/v1/admin/partners/${partnerId}/donation-matching/config/donation_date_eligibility`
      ),
    getPartnerDonationMatchingDirectToCampaignConfig: ({ partnerId }: { partnerId: string }) =>
      percentClient.get<PBPGetResponse<PartnerConfigRecord<DonationMatchingDirectToCampaignConfigValue>>>(
        `/v1/admin/partners/${partnerId}/donation-matching/config/direct_to_campaign_match_requests`
      ),
    getPartnerGivingThemeConfig: ({ partnerId }: { partnerId: string }) =>
      percentClient.get<PBPGetResponse<PartnerConfigRecord<GivingThemeConfigValue>>>(
        `/v1/admin/partners/${partnerId}/giving/config/theme`
      ),
    getPartnerGivingSupportedCurrenciesConfig: ({ partnerId }: { partnerId: string }) =>
      percentClient.get<PBPGetResponse<PartnerConfigRecord<string[]>>>(
        `/v1/admin/partners/${partnerId}/giving/config/supported_currencies`
      ),
    getPartnerGivingHomePageBannerContentConfig: ({ partnerId }: { partnerId: string }) =>
      percentClient.get<PBPGetResponse<PartnerConfigRecord<GivingHomePageBannerContentConfigValue>>>(
        `/v1/admin/partners/${partnerId}/giving/config/home_page_banner_content`
      ),
    getPartnerGivingRegionsConfig: ({ partnerId }: { partnerId: string }) =>
      percentClient.get<PBPGetResponse<PartnerConfigRecord<GivingRegionsConfigValue>>>(
        `/v1/admin/partners/${partnerId}/giving/config/regions`
      ),
    getPartnerGivingCalendarIntegrationConfig: ({ partnerId }: { partnerId: string }) =>
      percentClient.get<PBPGetResponse<PartnerConfigRecord<string[]>>>(
        `/v1/admin/partners/${partnerId}/giving/config/calendar_integration`
      ),
    getPartnerGivingHelpLinkConfig: ({ partnerId }: { partnerId: string }) =>
      percentClient.get<PBPGetResponse<PartnerConfigRecord<GivingHelpLinkConfigValue>>>(
        `/v1/admin/partners/${partnerId}/giving/config/help_link`
      ),
    getPartnerGivingPreferredCountriesConfig: ({ partnerId }: { partnerId: string }) =>
      percentClient.get<PBPGetResponse<PartnerConfigRecord<string[]>>>(
        `/v1/admin/partners/${partnerId}/giving/config/preferred_countries`
      ),
    getPartnerGivingRequireOrganisationTimeLogConfig: ({ partnerId }: { partnerId: string }) =>
      percentClient.get<PBPGetResponse<PartnerConfigRecord<boolean>>>(
        `/v1/admin/partners/${partnerId}/giving/config/require_organisation_time_log`
      ),
    getPartnerGivingSpotlightedOrgsConfig: ({ partnerId }: { partnerId: string }) =>
      percentClient.get<PBPGetResponse<PartnerConfigRecord<GivingSpotlightedOrgsConfigValue>>>(
        `/v1/admin/partners/${partnerId}/giving/config/spotlighted_orgs`
      ),
    getPartnerGivingFeaturedConfig: ({ partnerId }: { partnerId: string }) =>
      percentClient.get<PBPGetResponse<PartnerConfigRecord<GivingFeaturedConfigValue>>>(
        `/v1/admin/partners/${partnerId}/giving/config/featured`
      ),
    getPartnerDonationRestrictedCountriesConfig: ({ partnerId }: { partnerId: string }) =>
      percentClient.get<PBPGetResponse<PartnerConfigRecord<DonationRestrictedCountriesConfigValue>>>(
        `/v1/admin/partners/${partnerId}/donation/config/restricted_countries_for_donation`
      ),
    getPartnerGivingDatePeriodConfig: ({ partnerId }: { partnerId: string }) =>
      percentClient.get<PBPGetResponse<PartnerConfigRecord<GivingDatePeriodConfigurationValue>>>(
        `/v1/admin/partners/${partnerId}/giving/config/date_period_configuration`
      ),
    getPartnerVolunteeringAllowanceConfig: ({ partnerId }: { partnerId: string }) =>
      percentClient.get<PBPGetResponse<PartnerConfigRecord<VolunteeringAllowanceConfigValue>>>(
        `/v1/admin/partners/${partnerId}/volunteering/config/volunteering_allowance`
      ),
    getPartnerGamificationAchievementResetDateConfig: ({ partnerId }: { partnerId: string }) =>
      percentClient.get<PBPGetResponse<PartnerConfigRecord<string>>>(
        `/v1/admin/partners/${partnerId}/gamification/config/achievement_reset_date`
      )
  },
  commands: {
    createPartnerDeveloper: ({ payload, partnerId }: { payload: object; partnerId: string }) =>
      percentClient.post(`/v1/admin/partners/${partnerId}/accounts`, payload),
    acceptValidation: ({ payload, validationRequestId }: { payload: object; validationRequestId: string }) =>
      percentClient.post(`v1/admin/validation-requests/${validationRequestId}/accept`, payload),
    deletePartnerDeveloper: ({ id: { partnerId, memberId } }: any) =>
      percentClient.delete(`/v1/admin/partners/${partnerId}/accounts/${memberId}`),
    createPartner: (payload: { name: string; currencyCode: CurrencyCode }) =>
      percentClient.post('/v1/admin/partners', payload),
    deletePartner: ({ id }: { id: string }) => percentClient.delete(`/v1/admin/partners/${id}`),
    deleteStaticLink: ({ id }: { id: string }) => percentClient.delete(`/v1/admin/static-links/${id}`),
    addPartnerScope: ({ scope, id }: { scope: PartnerScopes; id: string }) =>
      percentClient.put(`/v1/admin/partners/${id}/scopes/${scope}/add`),
    removePartnerScope: ({ scope, id }: { scope: PartnerScopes; id: string }) =>
      percentClient.put(`/v1/admin/partners/${id}/scopes/${scope}/remove`),
    editPartnerDetail: ({ payload, partnerId }: any) => percentClient.patch(`/v1/admin/partners/${partnerId}`, payload),
    postOrganisationLogo: ({ payload, organisationId }: PostOrganisationLogoProps) =>
      percentClient
        .post<PBPGetResponse<OrganisationLogoBody>>(`/v1/admin/organisations/${organisationId}/logo`, payload.formData)
        .then(getDataFromPBPResponse),
    createOrganization: ({ payload }: CreateOrganizationProps) =>
      percentClient.post(`/v1/admin/validation-requests`, payload.formData),
    unclaimOrganization: (id: string) => percentClient.post(`v1/admin/organisations/${id}/unclaim`),
    organisationsBatchUpdate: ({ payload }: OrganisationsBatchUpdateProps) =>
      percentClient.post(`/v1/admin/organisations/batch-update`, payload),
    createStaticLink: ({ partnerId, payload }: { partnerId: string; payload: CreateStaticLinkPayload }) =>
      percentClient.post(`/v1/admin/partners/${partnerId}/static-links`, payload),
    updateStaticLink: ({ id, payload }: { id: string; payload: UpdateStaticLinkPayload }) =>
      percentClient.patch(`/v1/admin/static-links/${id}`, payload),
    // INTERNAL: prefer the typed save handlers in usePartnerGivingConfigs — this command's key/value are not correlated at the type level.
    updatePartnerGivingConfig: ({ partnerId, key, value }: UpdateGivingConfigPayload) =>
      percentClient.put(`/v1/admin/partners/${partnerId}/giving/config`, { key, value }),
    updatePartnerDonationConfig: ({ partnerId, key, value }: UpdateDonationConfigPayload) =>
      percentClient.put(`/v1/admin/partners/${partnerId}/donation/config`, { key, value }),
    updatePartnerFundraisingConfig: ({ partnerId, key, value }: UpdateFundraisingConfigPayload) =>
      percentClient.put(`/v1/admin/partners/${partnerId}/fundraising/config`, { key, value }),
    updatePartnerDonationMatchingConfig: ({ partnerId, key, value }: UpdateDonationMatchingConfigPayload) =>
      percentClient.put(`/v1/admin/partners/${partnerId}/donation-matching/config`, { key, value })
  }
})
